'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  childLevel,
  headingLevel,
  items,
  SkImage,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type SkImageRef,
  type Surface,
} from '../shared/primitives';
import { useSavedItems } from '../shared/savedItems';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Featured ranges: four cards, each with an image, the range name, a one-line
 * style description, an "Explore" link and a Save icon button.
 *
 * <p>The Save button is a real toggle (`aria-pressed`) with an accessible name
 * that includes the range ("Save Linden shaker"), so a list of four identical
 * heart icons is still distinguishable by ear. Saved ranges are kept in this
 * browser (see `savedItems.ts`). Without JavaScript the button is inert but
 * the card, its name and its Explore link all still work.
 */
export interface SpatialKitchenRange {
  /** Stable id for saving; defaults to the href or the name. */
  id?: string;
  name?: Localized;
  style?: Localized;
  href?: string;
  image?: SkImageRef;
}

export interface SpatialKitchenFeaturedRangesProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  ranges?: SpatialKitchenRange[];
  exploreLabel?: Localized;
  saveLabel?: Localized;
  linkLabel?: Localized;
  linkHref?: string;
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-ranges__head{display:flex;flex-wrap:wrap;align-items:end;justify-content:space-between;gap:1rem 2rem;margin-bottom:clamp(2rem,1.5rem + 2vw,3rem)}
.sk-ranges__head .sk-section-head{margin-bottom:0}
.sk-ranges__grid{display:grid;grid-template-columns:minmax(0,1fr);gap:2rem 1rem}
@media (min-width:480px){.sk-ranges__grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (min-width:1024px){.sk-ranges__grid{grid-template-columns:repeat(4,minmax(0,1fr));gap:2.5rem clamp(1rem,1.75vw,1.75rem)}}
.sk-range{display:grid;gap:.75rem;align-content:start}
.sk-range__media{position:relative;aspect-ratio:4/5;border-radius:8px}
.sk-range__save{position:absolute;top:.75rem;right:.75rem;display:inline-grid;place-items:center;width:2.75rem;height:2.75rem;border-radius:999px;border:1px solid transparent;background:rgba(255,255,255,.94);color:var(--sk-ink);cursor:pointer;transition:background-color .18s ease,color .18s ease}
.sk-range__save:hover{background:#fff;border-color:var(--sk-ink)}
.sk-range__save[aria-pressed="true"]{background:var(--sk-sage-deep);color:#fff}
.sk-range__save[aria-pressed="true"] svg{fill:currentColor}
.sk-range__name{font-size:clamp(1.25rem,1.1rem + .4vw,1.5rem)}
.sk-range__name a{color:inherit;text-decoration:none}
.sk-range__name a:hover{text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:.3em}
.sk-range__style{margin:-.25rem 0 0;color:var(--sk-muted);font-size:.9375rem}
`;

function RangeCard({
  range,
  index,
  exploreLabel,
  saveLabel,
  CardTitle,
  saved,
  onToggle,
}: {
  range: SpatialKitchenRange;
  index: number;
  exploreLabel: string;
  saveLabel: string;
  CardTitle: 'h2' | 'h3' | 'h4';
  saved: boolean;
  onToggle: () => void;
}) {
  const name = text(range.name, `Range ${index + 1}`);
  const style = text(range.style);
  return (
    <li className="sk-range">
      <div className="sk-range__media sk-media">
        <SkImage image={range.image ? { ...range.image, alt: '' } : undefined} sizes="(min-width: 1024px) 25vw, 50vw" />
        <button
          type="button"
          className="sk-range__save"
          aria-pressed={saved}
          // A toggle keeps ONE name; aria-pressed carries the state.
          aria-label={`${saveLabel} ${name}`}
          onClick={onToggle}
        >
          <Icon name="heart" size={20} />
        </button>
      </div>
      <CardTitle className="sk-heading sk-range__name">
        {range.href ? <SkLink href={range.href}>{name}</SkLink> : name}
      </CardTitle>
      {style && <p className="sk-range__style">{style}</p>}
      {range.href && (
        <p style={{ margin: 0 }}>
          <SkLink href={range.href} className="sk-textlink" aria-label={`${exploreLabel} ${name}`}>
            {exploreLabel}
            <Icon name="arrow-right" size={16} />
          </SkLink>
        </p>
      )}
    </li>
  );
}

export function SpatialKitchenFeaturedRanges(props: SpatialKitchenFeaturedRangesProps) {
  const ranges = items<SpatialKitchenRange>(props.ranges).slice(0, 4);
  const title = text(props.title, 'Featured ranges');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = childLevel(level);
  const exploreLabel = text(props.exploreLabel, 'Explore');
  const saveLabel = text(props.saveLabel, 'Save');
  const linkLabel = text(props.linkLabel);
  const titleId = useId();
  const { isSaved, toggle } = useSavedItems();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-ranges sk-section ${surfaceClass(props.surface, 'white')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <SkStyles id="featured-ranges" css={CSS} />
      <div className="sk-container">
        <div className="sk-ranges__head">
          <div className="sk-section-head">
            {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
            <Title id={titleId} className="sk-heading sk-h2">
              {title}
            </Title>
            {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
          </div>
          {linkLabel && props.linkHref && (
            <SkLink href={props.linkHref} className="sk-textlink">
              {linkLabel}
              <Icon name="arrow-right" size={18} />
            </SkLink>
          )}
        </div>
        <ul className="sk-ranges__grid sk-list-reset">
          {ranges.map((range, index) => {
            const name = text(range.name, `Range ${index + 1}`);
            const id = range.id || range.href || name;
            return (
              <RangeCard
                key={`${id}-${index}`}
                range={range}
                index={index}
                exploreLabel={exploreLabel}
                saveLabel={saveLabel}
                CardTitle={CardTitle}
                saved={isSaved(id)}
                onToggle={() => toggle({ id, name, href: range.href })}
              />
            );
          })}
        </ul>
      </div>
    </BlockRoot>
  );
}
